import { UserRole } from "@edlab-fms/shared";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthQuery } from "../lib/api/auth";
import { useProgrammesOverviewQuery, type ProgrammeUnitSummary } from "../lib/api/overview";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";
import { formatMoney } from "../lib/format/money";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Chip, ChipGroup } from "../components/ui/Chip";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { MonthSelector } from "../components/ui/MonthSelector";
import { PageHeader } from "../components/ui/PageHeader";
import { UnitMark } from "../components/ui/UnitMark";
import { AddUnitModal } from "../features/programmes/AddUnitModal";
import { ProgrammeUnitDetailModal } from "../features/programmes/ProgrammeUnitDetailModal";

function groupChipLabel(code: string, name: string): string {
  return code === "dlp" ? "DLP" : name;
}

function budgetUsedPct(ytdPaid: string | undefined, annualBudget: string): number {
  const budget = Number.parseFloat(annualBudget);
  if (budget <= 0) {
    return 0;
  }
  return Math.min(100, (Number.parseFloat(ytdPaid ?? "0") / budget) * 100);
}

export function ProgrammesPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const { period, setPeriod, months } = useReportingPeriod();
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [selectedUnit, setSelectedUnit] = useState<ProgrammeUnitSummary | null>(null);
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const programmes = useProgrammesOverviewQuery(true, period);

  const visibleGroups = useMemo(() => {
    if (!programmes.data) {
      return [];
    }
    if (groupFilter === "all") {
      return programmes.data.groups;
    }
    return programmes.data.groups.filter((group) => group.id === groupFilter);
  }, [groupFilter, programmes.data]);

  return (
    <div>
      <PageHeader
        eyebrow="Programmes"
        title="Your programmes. One foundation."
        description="Explore schools, centres, labs and education programmes within your authorized scope."
        actions={
          <>
            <MonthSelector value={period} onChange={setPeriod} months={months} />
            {user?.role === UserRole.SUPER_ADMIN ? (
              <Button
                onClick={() => {
                  setAddUnitOpen(true);
                }}
              >
                Add unit
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <ChipGroup>
          <Chip
            active={groupFilter === "all"}
            onClick={() => {
              setGroupFilter("all");
            }}
          >
            All programmes
          </Chip>
          {programmes.data?.groups.map((group) => (
            <Chip
              key={group.id}
              active={groupFilter === group.id}
              onClick={() => {
                setGroupFilter(group.id);
              }}
            >
              {groupChipLabel(group.code, group.name)}
            </Chip>
          ))}
        </ChipGroup>
        <Link to="/?scroll=programme-financials">
          <Button variant="secondary">Financial table</Button>
        </Link>
      </div>

      {programmes.isLoading ? (
        <LoadingSpinner label="Loading programmes…" className="py-16" />
      ) : programmes.isError ? (
        <EmptyState
          title="Could not load programmes"
          description="Check that the API is running and try again."
        />
      ) : programmes.data?.groups.length ? (
        <div className="grid gap-[18px] sm:grid-cols-2 xl:grid-cols-3">
          {visibleGroups.flatMap((group) =>
            group.units.map((unit) => {
              const usedPct = budgetUsedPct(unit.ytdPaid, unit.annualBudget);
              return (
                <Card key={unit.id} className="flex flex-col gap-[18px]">
                  <div className="flex items-center gap-[11px]">
                    <UnitMark groupCode={group.code} label={unit.code} />
                    <div>
                      <h2 className="text-base font-semibold text-edlab-ink">{unit.name}</h2>
                      <p className="text-xs text-edlab-muted">{group.name}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <small className="block text-xs text-[#86909b]">YTD received</small>
                      <b className="mt-[5px] block text-[19px] font-semibold tracking-[-0.5px] text-edlab-ink">
                        {formatMoney(unit.ytdIncome ?? "0")}
                      </b>
                    </div>
                    <div className="text-right">
                      <small className="block text-xs text-[#86909b]">YTD paid</small>
                      <b className="mt-[5px] block text-[19px] font-semibold tracking-[-0.5px] text-edlab-ink">
                        {formatMoney(unit.ytdPaid ?? "0")}
                      </b>
                    </div>
                  </div>
                  <div>
                    <div className="h-1.5 overflow-hidden rounded-lg bg-[#edf1f3]">
                      <div
                        className="h-full rounded-lg bg-[#2b9177]"
                        style={{ width: `${usedPct}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-[#7c8693]">
                      <span>{usedPct.toFixed(1)}% of budget</span>
                      <span>{formatMoney(unit.annualBudget)}</span>
                    </div>
                  </div>
                  <div className="mt-auto border-t border-edlab-line pt-4">
                    <Button
                      variant="ghost"
                      className="px-0 text-edlab-green hover:bg-transparent"
                      onClick={() => {
                        setSelectedUnit(unit);
                      }}
                    >
                      View programme →
                    </Button>
                  </div>
                </Card>
              );
            }),
          )}
        </div>
      ) : (
        <EmptyState
          title="No programmes assigned"
          description="Programme cards appear only for units you are authorized to access."
        />
      )}

      <AddUnitModal
        open={addUnitOpen}
        onClose={() => {
          setAddUnitOpen(false);
        }}
      />

      {selectedUnit ? (
        <ProgrammeUnitDetailModal
          unit={selectedUnit}
          period={period}
          onClose={() => {
            setSelectedUnit(null);
          }}
        />
      ) : null}
    </div>
  );
}
